import { mix } from '../icons/pix';
import type { Layer } from './grid';
import {
  burst,
  crescent,
  type Ctx,
  cloud,
  dot,
  drop,
  fistHand,
  M,
  palmHand,
  sabre,
  SCENES,
  type Scene,
  spark,
  speedLines,
  sword,
  yinyang,
} from './scenes';

/**
 * More pictures, one idea each. `SCENES` (scenes.ts) holds the base set; these are the variants
 * the moves ask for by name in `recipes.ts`.
 */

const RUN = ['..kk..', '..kk..', '.DDDD.', 'DDDDDD', '.DDDD.', '.D..D.', 'D....D'];

function runner(L: Layer, x0: number, y0: number, skin: string, robe: string): void {
  L.sprite(x0, y0, RUN, { k: skin, D: robe });
}

function ghostRun(L: Layer, x: Ctx, y0 = 12): void {
  const g1 = mix(x.t.d, M.ink, 0.5);
  const g2 = mix(x.t.d, M.ink, 0.2);
  runner(L, 2, y0, g1, g1);
  runner(L, 6, y0, g2, g2);
  runner(L, 10, y0, M.skin, x.t.l);
}

const more: Record<string, Scene> = {
  boltBig: (L, x) => {
    sword(L, 7, 21, 12, 14);
    L.poly([[14, 1], [9, 8], [14, 9], [8, 15]], M.yellow, 3);
    L.poly([[14, 1], [9, 8], [14, 9], [8, 15]], '#ffffff', 1);
    burst(L, 8, 17, 3, 6, 8, x.t.hi, x.t.l);
    spark(L, 19, 7, 2, x.t.l);
    spark(L, 4, 7, 1, x.t.l);
  },
  lunge: (L, x) => {
    speedLines(L, [[9, 6, 5], [7, 11, 4], [10, 19, 5]], x.t.l);
    sword(L, 3, 20, 19, 6);
    spark(L, 20, 5, 2, x.t.hi);
  },
  lungeSabre: (L, x) => {
    speedLines(L, [[10, 7, 6], [9, 17, 6], [8, 21, 4]], M.blood);
    sabre(L, 2, 12, 20, 11, M.steel, 1.5);
    spark(L, 21, 11, 2, x.t.hi);
  },
  fan6: (L, x) => {
    for (let i = 0; i < 6; i++) {
      const a = -1.3 + i * 0.52;
      const ex = 5 + Math.cos(a) * 17;
      const ey = 18 + Math.sin(a) * 17;
      L.line(5, 18, ex, ey, i % 2 ? x.t.l : x.t.hi, 1);
      L.set(ex, ey, '#ffffff');
    }
    L.rect(3, 17, 4, 4, M.skin);
    L.rect(3, 21, 4, 1, M.gold);
  },
  knives: (L, x) => {
    for (const [px, py, a] of [[6, 18, -1.0], [10, 19, -0.6], [14, 18, -0.2]] as const) {
      sword(L, px, py, px + Math.cos(a) * 11, py + Math.sin(a) * 11, M.steelMid);
    }
    spark(L, 20, 6, 1, x.t.hi);
  },
  rain: (L, x) => {
    for (const [px, py] of [[5, 3], [9, 6], [13, 2], [17, 5], [7, 11], [11, 13], [15, 10], [19, 12]] as const) {
      L.line(px, py, px - 1, py + 4, M.steelMid, 1);
      L.set(px - 1, py + 5, x.t.hi);
    }
    L.line(3, 21, 20, 21, x.t.d);
  },
  caltrops: (L, x) => {
    for (const [cx, cy] of [[7, 17], [15, 17], [11, 9]] as const) {
      L.disc(cx, cy, 2, M.steelDark);
      for (const [dx, dy] of [[0, -4], [0, 4], [-4, 0], [4, 0], [3, 3], [-3, -3]] as const) L.line(cx, cy, cx + dx, cy + dy, M.steel, 1);
    }
    L.line(3, 21, 20, 21, x.t.d);
    spark(L, 19, 5, 1, x.t.hi);
  },
  shuriken: (L, x) => {
    for (const [cx, cy, r] of [[8, 14, 5], [17, 7, 3]] as const) {
      L.fillPoly([[cx, cy - r], [cx + 1, cy - 1], [cx + r, cy], [cx + 1, cy + 1], [cx, cy + r], [cx - 1, cy + 1], [cx - r, cy], [cx - 1, cy - 1]], M.steel);
      L.set(cx, cy, x.t.c);
    }
    speedLines(L, [[7, 6, 4], [12, 20, 5]], x.t.l);
  },
  snare: (L, x) => {
    L.arc(12, 12, 6, 0.4, 6.0, M.steelMid, 1);
    L.arc(12, 12, 4, 0.4, 6.0, x.t.l, 1);
    L.line(18, 15, 21, 20, M.steelMid, 1);
    L.line(6, 16, 3, 21, M.steelMid, 1);
    L.rect(17, 4, 3, 3, M.steelDark);
    L.line(16, 7, 14, 10, M.steelMid);
    spark(L, 4, 5, 1, x.t.hi);
  },
  palm2: (L, x) => {
    palmHand(L, 2, 6);
    L.arc(8, 12, 9, -0.8, 0.8, x.t.l);
    L.arc(8, 12, 11, -0.7, 0.7, x.t.c);
    L.arc(8, 12, 13, -0.6, 0.6, x.t.d);
    spark(L, 20, 3, 1, x.t.hi);
  },
  palmHalo: (L, x) => {
    L.arc(12, 11, 9, 0, 6.3, M.gold, 1);
    burst(L, 12, 11, 9, 11, 12, M.gold, M.goldDark);
    palmHand(L, 6, 5);
    void x;
  },
  palms3: (L, x) => {
    palmHand(L, 1, 9);
    palmHand(L, 9, 3);
    palmHand(L, 5, 5);
    spark(L, 20, 18, 2, x.t.hi);
  },
  palmCloud: (L, x) => {
    cloud(L, 2, 15, 10, x.t.l, x.t.d);
    cloud(L, 11, 17, 10, x.t.hi, x.t.c);
    cloud(L, 4, 19, 14, x.t.l, x.t.d);
    palmHand(L, 6, 1);
  },
  palmDragon: (L, x) => {
    palmHand(L, 2, 6);
    L.curve(14, 20, 22, 14, 18, 4, M.gold, 2);
    L.curve(14, 20, 22, 14, 18, 4, x.t.hi, 1);
    L.fillPoly([[17, 2], [21, 3], [20, 6], [16, 5]], M.gold);
    L.set(19, 3, M.red);
  },
  palmDark: (L, x) => {
    L.disc(12, 12, 9, M.ink);
    L.arc(12, 12, 9, 0, 6.3, x.t.l, 1);
    L.arc(12, 12, 6, 1, 5, x.t.c, 1);
    palmHand(L, 6, 5);
  },
  fistWheel: (L, x) => {
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * 6.283;
      L.disc(12 + Math.cos(a) * 8, 12 + Math.sin(a) * 8, 1.6, M.skin);
    }
    fistHand(L, 6, 6);
    L.arc(12, 12, 8, 0, 6.3, M.gold, 1);
    void x;
  },
  foot: (L, x) => {
    speedLines(L, [[8, 8, 6], [7, 14, 5]], x.t.l);
    L.fillPoly([[9, 9], [17, 9], [20, 15], [20, 18], [8, 18], [8, 12]], M.cloth);
    L.rect(8, 18, 12, 3, M.woodDark);
    L.rect(9, 10, 8, 1, M.steelMid);
    burst(L, 19, 12, 2, 4, 6, M.gold, M.goldDark);
  },
  stomp: (L, x) => {
    L.fillPoly([[8, 2], [16, 2], [17, 8], [17, 11], [7, 11], [7, 5]], M.cloth);
    L.rect(7, 11, 10, 2, M.woodDark);
    L.poly([[12, 14], [9, 17], [12, 19], [8, 22]], x.t.hi, 1);
    L.poly([[12, 14], [16, 17], [14, 20], [19, 22]], x.t.hi, 1);
    L.rect(3, 19, 3, 2, x.t.d);
    L.rect(19, 18, 2, 2, x.t.d);
  },
  slamSword: (L, x) => {
    sword(L, 12, 1, 12, 15);
    L.poly([[12, 16], [8, 18], [10, 21]], x.t.hi, 1);
    L.poly([[12, 16], [16, 18], [15, 21]], x.t.hi, 1);
    L.rect(3, 17, 3, 3, x.t.d);
    L.rect(18, 18, 3, 2, x.t.d);
    L.line(3, 22, 20, 22, x.t.d);
  },
  club: (L, x) => {
    L.line(5, 20, 14, 8, M.wood, 3);
    L.line(6, 20, 15, 8, M.woodDark, 1);
    L.disc(15, 7, 3, M.wood);
    burst(L, 18, 5, 2, 5, 8, x.t.hi, x.t.l);
    L.rect(4, 21, 8, 1, x.t.d);
  },
  boulder: (L, x) => {
    L.fillPoly([[6, 14], [9, 8], [15, 7], [19, 12], [17, 18], [9, 19]], M.steelDark);
    L.fillPoly([[9, 9], [14, 8], [17, 11], [12, 12], [9, 13]], M.steelMid);
    burst(L, 12, 13, 8, 10, 8, x.t.hi, x.t.l);
  },
  sabreBreak: (L, x) => {
    L.fillPoly([[5, 4], [19, 4], [19, 12], [12, 21], [5, 12]], M.steelMid);
    L.fillPoly([[7, 6], [17, 6], [17, 11], [12, 18], [7, 11]], M.steelDark);
    L.poly([[11, 4], [13, 9], [10, 13], [13, 20]], null, 2);
    sabre(L, 3, 21, 20, 3, M.steel, 2);
    spark(L, 20, 3, 1, x.t.hi);
  },
  dragonWave: (L, x) => {
    crescent(L, 5, 20, 16, -1.45, 0.05, M.gold, M.goldDark);
    crescent(L, 5, 20, 12, -1.4, -0.05, x.t.hi, x.t.c);
    sabre(L, 3, 21, 11, 13);
    L.fillPoly([[17, 2], [21, 4], [20, 7], [16, 6]], M.gold);
    L.set(19, 4, M.red);
  },
  combo: (L, x) => {
    crescent(L, 6, 16, 9, -1.9, -0.3, x.t.hi, x.t.l);
    crescent(L, 12, 18, 9, -1.9, -0.3, x.t.l, x.t.c);
    crescent(L, 16, 20, 7, -1.9, -0.3, x.t.hi, x.t.l);
    sabre(L, 3, 21, 9, 12, M.steel, 1);
    spark(L, 20, 6, 1, x.t.hi);
  },
  crossX: (L, x) => {
    sabre(L, 3, 21, 20, 4, M.steel, 2);
    sabre(L, 20, 21, 3, 4, M.steel, -2);
    burst(L, 12, 12, 2, 4, 8, x.t.hi, x.t.l);
  },
  chop: (L, x) => {
    sabre(L, 12, 1, 12, 17, M.steel, 2);
    L.arc(12, 19, 8, 0.1, 3.0, x.t.hi);
    L.arc(12, 19, 10, 0.2, 2.9, x.t.l);
    drop(L, 4, 12, x.t.l, x.t.hi);
    drop(L, 20, 12, x.t.l, x.t.hi);
  },
  reap: (L, x) => {
    crescent(L, 14, 13, 10, 2.2, 5.0, x.t.hi, x.t.l);
    crescent(L, 14, 13, 7, 2.3, 4.8, x.t.c, x.t.d);
    L.line(3, 21, 10, 14, M.wood, 2);
    spark(L, 5, 4, 1, x.t.hi);
  },
  ribbon: (L, x) => {
    L.curve(3, 19, 8, 6, 12, 12, M.steel, 2);
    L.curve(12, 12, 17, 18, 20, 5, M.steel, 2);
    L.curve(3, 20, 8, 10, 12, 13, x.t.l, 1);
    L.curve(12, 13, 17, 20, 20, 7, x.t.l, 1);
    spark(L, 20, 4, 1, x.t.hi);
  },
  bloodTide: (L, x) => {
    for (let i = 0; i < 3; i++) L.curve(2, 10 + i * 4, 7, 5 + i * 4, 12, 10 + i * 4, i === 1 ? M.bloodDark : M.blood, 2);
    L.curve(12, 10, 17, 15, 21, 10, M.blood, 2);
    L.curve(12, 14, 17, 19, 21, 14, M.bloodDark, 2);
    drop(L, 18, 5, M.blood);
    void x;
  },
  bloodDrink: (L, x) => {
    L.fillPoly([[5, 12], [19, 12], [17, 19], [7, 19]], M.gold);
    L.rect(6, 12, 12, 2, M.blood);
    L.rect(9, 19, 6, 2, M.goldDark);
    drop(L, 12, 5, M.blood);
    spark(L, 19, 5, 1, x.t.hi);
  },
  moonSword: (L, x) => {
    L.disc(12, 11, 8, M.violet);
    L.disc(15, 9, 7, null);
    sword(L, 3, 21, 20, 6);
    spark(L, 6, 5, 1, x.t.hi);
  },
  veil: (L, x) => {
    L.fillPoly([[4, 4], [20, 4], [18, 12], [20, 21], [4, 21], [6, 12]], x.t.d);
    L.fillPoly([[7, 6], [17, 6], [15, 12], [17, 19], [7, 19], [9, 12]], x.t.c);
    L.line(12, 6, 12, 19, x.t.l);
    spark(L, 18, 6, 1, x.t.hi);
  },
  eclipse: (L, x) => {
    L.disc(12, 12, 7, M.ink);
    L.arc(12, 12, 8, 0, 6.3, x.t.hi, 1);
    L.arc(12, 12, 9, 3.7, 6.2, x.t.l, 1);
    burst(L, 12, 12, 9, 11, 10, M.violet, x.t.c);
    L.disc(14, 10, 2, mix(M.ink, x.t.d, 0.3));
  },
  sleeve: (L, x) => {
    L.curve(3, 18, 8, 2, 14, 12, M.cloth, 3);
    L.curve(14, 12, 18, 18, 21, 6, M.cloth, 3);
    L.curve(3, 20, 8, 4, 14, 13, x.t.l, 1);
    L.curve(14, 13, 18, 20, 21, 8, x.t.l, 1);
    spark(L, 20, 4, 1, x.t.hi);
  },
  charmEye: (L, x) => {
    L.fillPoly([[2, 12], [8, 6], [16, 6], [22, 12], [16, 18], [8, 18]], M.cloth);
    L.disc(12, 12, 4, x.t.c);
    L.disc(12, 12, 2, M.ink);
    L.set(10, 10, '#ffffff');
    L.arc(12, 12, 4, 0.5, 4, x.t.hi);
    spark(L, 19, 4, 1, x.t.hi);
    spark(L, 4, 19, 1, x.t.hi);
  },
  soul: (L, x) => {
    L.fillPoly([[12, 2], [18, 9], [19, 20], [16, 17], [12, 21], [8, 17], [5, 20], [6, 9]], x.t.hi);
    L.rect(8, 10, 3, 3, M.ink);
    L.rect(13, 10, 3, 3, M.ink);
    L.rect(11, 15, 2, 2, M.ink);
    spark(L, 20, 4, 1, x.t.l);
  },
  drainHand: (L, x) => {
    palmHand(L, 2, 9);
    drop(L, 16, 5, M.blood);
    L.arc(14, 12, 6, -1.5, 0.6, x.t.l);
    L.arc(14, 12, 4, -1.2, 0.8, x.t.hi);
  },
  bloom: (L, x) => {
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * 6.283 - 1.57;
      L.disc(12 + Math.cos(a) * 5, 12 + Math.sin(a) * 5, 3, i % 2 ? '#ffb0d8' : '#ff8ac0');
    }
    L.disc(12, 12, 2, M.gold);
    spark(L, 20, 4, 1, x.t.hi);
  },
  poem: (L, x) => {
    L.rect(4, 3, 12, 18, M.cloth);
    for (let i = 0; i < 4; i++) L.rect(6, 6 + i * 4, 8 - (i % 2) * 2, 1, M.ink);
    L.line(12, 14, 20, 4, M.wood, 2);
    L.set(11, 15, x.t.d);
    L.line(11, 15, 13, 12, M.ink, 2);
  },
  brushBreak: (L, x) => {
    L.curve(3, 18, 12, 4, 20, 17, x.t.l, 3);
    L.curve(3, 18, 12, 4, 20, 17, x.t.hi, 1);
    L.poly([[12, 8], [10, 12], [13, 14], [11, 20]], null, 2);
    drop(L, 5, 21, x.t.d, x.t.l);
  },
  scrollOpen: (L, x) => {
    L.rect(3, 6, 18, 12, M.cloth);
    L.disc(3, 12, 3, M.wood);
    L.disc(21, 12, 3, M.wood);
    L.curve(5, 15, 10, 6, 14, 14, M.ink, 2);
    L.curve(14, 14, 17, 17, 19, 9, M.ink, 2);
    burst(L, 12, 12, 10, 12, 10, x.t.hi, x.t.l);
  },
  qin: (L, x) => {
    L.fillPoly([[2, 14], [18, 5], [21, 9], [5, 20]], M.wood);
    L.fillPoly([[4, 14], [18, 7], [19, 9], [6, 17]], M.woodDark);
    for (let i = 0; i < 4; i++) L.line(5 + i, 15 - i, 19 - i * 0, 8 + i * 0, i % 2 ? x.t.hi : M.cloth, 1);
    spark(L, 20, 18, 1, x.t.hi);
    dot(L, 16, 19, x.t.l);
    dot(L, 20, 14, x.t.l);
  },
  shout: (L, x) => {
    burst(L, 12, 12, 7, 11, 14, M.gold, M.goldDark);
    L.disc(12, 12, 6, M.goldDark);
    L.rect(7, 10, 10, 5, M.redDark);
    for (let i = 0; i < 4; i++) L.rect(8 + i * 2 + 0, 10, 1, 2, M.bone);
    L.arc(12, 12, 9, -0.6, 0.6, x.t.hi);
  },
  bellShield: (L, x) => {
    SCENES.bell!(L, x);
    L.arc(12, 12, 10, 0, 6.3, M.gold, 1);
  },
  swordRest: (L, x) => {
    sword(L, 12, 19, 12, 3);
    L.line(5, 20, 19, 20, x.t.l);
    L.line(7, 22, 17, 22, x.t.d);
    spark(L, 6, 7, 1, x.t.hi);
    spark(L, 18, 10, 1, x.t.hi);
  },
  zen: (L, x) => {
    L.arc(12, 9, 8, 0, 6.3, M.gold, 1);
    L.disc(12, 7, 2.5, M.skin);
    L.fillPoly([[12, 10], [17, 18], [7, 18]], x.t.l);
    L.rect(5, 18, 14, 3, x.t.c);
    L.rect(10, 12, 4, 2, M.skin);
  },
  flame: (L, x) => {
    L.fillPoly([[12, 2], [16, 9], [19, 15], [16, 21], [8, 21], [5, 15], [8, 9]], M.fire);
    L.fillPoly([[12, 8], [15, 14], [14, 20], [10, 20], [9, 14]], M.yellow);
    L.fillPoly([[12, 13], [13, 17], [12, 20], [11, 17]], '#ffffff');
    void x;
  },
  ghostStep: (L, x) => {
    ghostRun(L, x, 8);
    L.arc(12, 20, 8, 0.2, 2.9, x.t.l);
  },
  scales: (L, x) => {
    for (let r = 0; r < 3; r++)
      for (let c = 0; c < 3; c++) {
        const cx = 5 + c * 5 + (r % 2) * 2;
        const cy = 7 + r * 4;
        L.fillPoly([[cx, cy - 2], [cx + 3, cy], [cx, cy + 3], [cx - 3, cy]], (r + c) % 2 ? x.t.c : x.t.l);
        L.set(cx, cy - 1, x.t.hi);
      }
  },
  smoke: (L, x) => {
    cloud(L, 3, 13, 12, '#cfd6dd', '#8a96a2');
    cloud(L, 9, 8, 12, '#e8edf1', '#a7b1bb');
    cloud(L, 4, 17, 16, '#bfc7cf', '#7a8692');
    L.disc(8, 8, 2, M.ink);
    L.set(8, 7, M.steelMid);
    void x;
  },
  crosshair: (L, x) => {
    L.arc(12, 12, 7, 0, 6.3, x.t.hi, 1);
    for (const [a, b, c, d] of [[12, 2, 12, 8], [12, 16, 12, 22], [2, 12, 8, 12], [16, 12, 22, 12]] as const) L.line(a, b, c, d, x.t.l);
    L.disc(12, 12, 1, M.red);
  },
  mirror: (L, x) => {
    L.disc(12, 10, 7, M.goldDark);
    L.disc(12, 10, 5.5, M.steel);
    L.line(8, 13, 14, 6, '#ffffff', 2);
    L.rect(11, 17, 3, 5, M.gold);
    spark(L, 20, 4, 1, x.t.hi);
  },
  screen: (L, x) => {
    for (let i = 0; i < 3; i++) {
      L.rect(3 + i * 6, 4, 6, 16, i % 2 ? M.cloth : M.bone);
      L.rect(3 + i * 6, 4, 1, 16, M.woodDark);
    }
    L.rect(3, 4, 18, 1, M.woodDark);
    L.rect(3, 19, 18, 1, M.woodDark);
    L.poly([[5, 16], [8, 11], [11, 14], [15, 8], [19, 15]], x.t.d, 1);
  },
  banner: (L, x) => {
    L.rect(4, 3, 16, 2, M.wood);
    L.rect(6, 5, 12, 15, M.cloth);
    L.rect(6, 17, 12, 3, M.red);
    L.rect(10, 8, 4, 6, M.red);
    L.rect(11, 9, 2, 4, M.cloth);
    L.rect(10, 20, 4, 2, M.gold);
    void x;
  },
  enso: (L, x) => {
    L.arc(12, 12, 7, 0.9, 6.4, M.cloth, 2);
    L.arc(12, 12, 7, 5.0, 6.4, x.t.hi, 1);
    drop(L, 15, 20, M.cloth, '#ffffff');
  },
  lantern: (L, x) => {
    L.rect(11, 2, 2, 3, M.steelDark);
    L.rect(7, 5, 10, 2, M.steelMid);
    L.fillPoly([[7, 7], [17, 7], [18, 12], [17, 17], [7, 17], [6, 12]], M.fire);
    L.rect(9, 8, 6, 8, M.yellow);
    L.rect(7, 17, 10, 2, M.steelMid);
    L.line(12, 7, 12, 17, M.steelDark);
    void x;
  },
  paw: (L, x) => {
    L.disc(12, 15, 5, x.t.d);
    L.disc(12, 15, 4, x.t.c);
    for (const [px, py] of [[5, 9], [9, 6], [15, 6], [19, 9]] as const) {
      L.disc(px, py, 2, x.t.c);
      L.line(px, py - 2, px - 1, py - 4, M.bone, 1);
    }
  },
  venomFang: (L, x) => {
    SCENES.bite!(L, x);
    drop(L, 12, 20, M.poison, '#dfffb0');
  },
  stinger: (L, x) => {
    L.curve(5, 21, 2, 8, 12, 5, x.t.c, 3);
    L.curve(12, 5, 17, 4, 18, 12, x.t.c, 3);
    L.poly([[18, 12], [19, 18]], M.bone, 2);
    drop(L, 19, 20, M.poison, '#dfffb0');
    L.curve(5, 21, 2, 8, 12, 5, x.t.l, 1);
  },
  pincer: (L, x) => {
    L.arc(7, 16, 7, -1.5, 0.3, x.t.c, 3);
    L.arc(17, 16, 7, 2.8, 4.6, x.t.c, 3);
    L.arc(7, 16, 7, -1.5, 0.3, x.t.l, 1);
    L.arc(17, 16, 7, 2.8, 4.6, x.t.l, 1);
    spark(L, 12, 6, 1, x.t.hi);
  },
  web: (L, x) => {
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * 6.283;
      L.line(12, 12, 12 + Math.cos(a) * 10, 12 + Math.sin(a) * 10, M.cloth, 1);
    }
    for (const r of [3, 6, 9]) L.arc(12, 12, r, 0, 6.3, x.t.l, 1);
    L.disc(12, 12, 1.5, M.ink);
  },
  coil: (L, x) => {
    L.arc(12, 14, 7, 0.2, 6.0, x.t.c, 3);
    L.arc(12, 14, 3, 1.5, 7.0, x.t.l, 2);
    L.arc(12, 14, 7, 0.2, 6.0, x.t.hi, 1);
    L.disc(18, 8, 2, x.t.c);
    L.set(19, 7, M.red);
  },
  tusk: (L, x) => {
    L.curve(6, 4, 2, 14, 10, 20, M.bone, 3);
    L.curve(18, 4, 22, 14, 14, 20, M.bone, 3);
    L.curve(6, 4, 3, 14, 10, 20, M.steel, 1);
    burst(L, 12, 18, 2, 5, 8, x.t.hi, x.t.l);
  },
  beak: (L, x) => {
    L.fillPoly([[4, 6], [20, 12], [4, 18], [8, 12]], M.gold);
    L.fillPoly([[5, 8], [15, 12], [6, 12]], M.steel);
    L.line(3, 4, 9, 9, x.t.l, 2);
    L.line(3, 20, 9, 15, x.t.l, 2);
    spark(L, 20, 5, 1, x.t.hi);
  },
  feather: (L, x) => {
    L.curve(4, 21, 8, 6, 20, 3, x.t.c, 5);
    L.curve(4, 21, 8, 6, 20, 3, x.t.hi, 1);
    L.line(4, 21, 20, 3, M.bone, 1);
    L.set(21, 2, '#ffffff');
    speedLines(L, [[10, 21, 5], [18, 17, 4]], x.t.l);
  },
  shell: (L, x) => {
    L.fillPoly([[3, 17], [5, 10], [10, 6], [14, 6], [19, 10], [21, 17]], x.t.c);
    L.line(12, 7, 12, 17, x.t.d);
    L.line(7, 12, 18, 12, x.t.d);
    L.line(4, 17, 20, 17, x.t.d);
    L.rect(4, 17, 16, 2, M.bone);
    L.set(9, 9, x.t.hi);
    L.set(10, 9, x.t.hi);
  },
  fin: (L, x) => {
    L.fillPoly([[7, 18], [10, 4], [18, 18]], M.steelMid);
    L.fillPoly([[10, 8], [10, 18], [16, 18]], M.steel);
    L.curve(2, 20, 6, 17, 10, 20, x.t.l, 1);
    L.curve(12, 21, 16, 18, 21, 21, x.t.l, 1);
  },
  wave: (L, x) => {
    L.curve(2, 16, 8, 3, 15, 12, x.t.l, 3);
    L.curve(15, 12, 17, 15, 21, 9, x.t.l, 2);
    L.line(2, 20, 21, 20, x.t.c, 2);
    spark(L, 19, 4, 1, x.t.hi);
  },
  harpoonHook: (L, x) => {
    SCENES.dgHarpoon!(L, x);
    L.arc(19, 17, 3, 3.5, 7.0, M.steelMid, 1);
  },
  dagger: (L, x) => {
    sword(L, 3, 20, 14, 8, M.steelMid);
    sword(L, 20, 20, 9, 8, M.steel);
    spark(L, 12, 5, 1, x.t.hi);
  },
  yinSword: (L, x) => {
    yinyang(L, 12, 14, 6);
    sword(L, 2, 22, 21, 3);
    void x;
  },
  taijiWheel: (L, x) => {
    L.arc(12, 12, 8, 0.2, 2.5, x.t.hi, 2);
    L.arc(12, 12, 8, 3.3, 5.6, x.t.hi, 2);
    L.poly([[17, 17], [20, 14], [15, 13]], x.t.l, 1);
    L.poly([[7, 7], [4, 10], [9, 11]], x.t.l, 1);
    yinyang(L, 12, 12, 4);
  },
  taijiHands: (L, x) => {
    yinyang(L, 12, 11, 5);
    L.curve(3, 18, 4, 11, 8, 8, M.skin, 3);
    L.curve(21, 18, 20, 11, 16, 8, M.skin, 3);
    L.line(5, 21, 19, 21, x.t.l);
    spark(L, 12, 3, 1, x.t.hi);
  },
  taijiBig: (L, x) => {
    burst(L, 12, 12, 8, 11, 16, x.t.hi, x.t.l);
    yinyang(L, 12, 12, 7);
  },
  petals: (L, x) => {
    sabre(L, 3, 21, 17, 7, M.steel, 2);
    for (const [px, py] of [[17, 14], [20, 9], [14, 18], [19, 18], [8, 6], [12, 4]] as const) {
      L.rect(px, py, 2, 2, '#ffb0d8');
      L.set(px, py, '#ff8ac0');
    }
    void x;
  },
  whirlPetals: (L, x) => {
    SCENES.whirl!(L, x);
    for (const [px, py] of [[5, 5], [19, 6], [18, 19], [4, 17]] as const) {
      L.rect(px, py, 2, 2, '#ffb0d8');
    }
  },
  whirlLeaf: (L, x) => {
    L.arc(12, 12, 8, 0.5, 4.4, M.jade, 2);
    for (const [px, py, a] of [[6, 6, 0], [18, 8, 1], [16, 18, 2], [5, 16, 3]] as const) {
      L.fillPoly([[px, py - 2], [px + 2, py], [px, py + 2], [px - 2, py]], a % 2 ? x.t.l : M.jade);
    }
    sword(L, 9, 15, 15, 9);
  },
  dew: (L, x) => {
    L.fillPoly([[12, 3], [17, 12], [17, 16], [12, 20], [7, 16], [7, 12]], M.ice);
    L.fillPoly([[12, 8], [15, 13], [14, 17], [12, 18], [10, 15]], x.t.c);
    L.set(9, 13, '#ffffff');
    L.set(9, 14, '#ffffff');
    spark(L, 19, 5, 1, x.t.hi);
  },
  blossomHeal: (L, x) => {
    SCENES.calm!(L, x);
  },
};

Object.assign(SCENES, more);
